import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartContext } from '../../../context/CartContext';
import { useRestaurantContext } from '../../../context/RestaurantContext';
import MoneyUtils from '../../../utils/moneyUtils';
import { capitalizeFirst } from '../../../utils/stringUtils';
import Icon from '../../../components/AppIcon';


// ─── Helper: build initial combo selections (first option pre-selected) ──────

const buildInitialComboSelections = (comboComponents) => {
  const selections = {};
  if (!comboComponents) return selections;
  comboComponents.forEach((component, idx) => {
    if (component.options && component.options.length > 0) {
      // Prefer free option (extraPrice === 0) first; fall back to index 0
      const free = component.options.find((o) => !o.extraPrice || o.extraPrice === 0);
      const chosen = free || component.options[0];
      selections[idx] = {
        optionName: chosen.name,
        extraPrice: chosen.extraPrice || 0,
        menuItemId: chosen.menuItemId || null,
      };
    }
  });
  return selections;
};

// ─── Combo Component Picker sub-component ────────────────────────────────────

const ComboComponentPicker = ({ component, componentIndex, selectedOption, onSelect, themeColor }) => {
  const hasOptions = component.options && component.options.length > 0;

  if (!hasOptions) {
    // Fixed component — display only
    return (
      <div className="flex items-center gap-2 py-1">
        <span className="text-orange-500 font-bold text-sm">{component.quantity ?? 1}×</span>
        <span className="text-sm text-gray-700 font-medium capitalize">{component.name}</span>
        {component.instructions && (
          <span className="text-[10px] text-gray-400 italic ml-auto capitalize">{component.instructions}</span>
        )}
      </div>
    );
  }

  // Selectable component
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-orange-500 font-bold text-sm">{component.quantity ?? 1}×</span>
        <span className="text-sm font-semibold text-gray-800 capitalize">{component.name}</span>
        <span className="text-[10px] text-gray-400 ml-auto">Choose One</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {component.options.map((option, optIdx) => {
          const isSelected = selectedOption?.optionName === option.name;
          return (
            <button
              key={optIdx}
              onClick={() =>
                onSelect(componentIndex, {
                  optionName: option.name,
                  extraPrice: option.extraPrice || 0,
                  menuItemId: option.menuItemId || null,
                })
              }
              className="flex items-center gap-1 px-3 py-2 rounded-xl border-2 text-sm capitalize transition-all"
              style={
                isSelected
                  ? {
                      borderColor: themeColor,
                      backgroundColor: `${themeColor}15`,
                      color: themeColor,
                      fontWeight: 600,
                    }
                  : { borderColor: '#e5e7eb', backgroundColor: '#fff', color: '#374151' }
              }
            >
              <span className="capitalize">{option.name}</span>
              {option.extraPrice > 0 && (
                <span
                  className="text-[10px] font-semibold px-1 py-0.5 rounded-full ml-1 normal-case"
                  style={isSelected ? { backgroundColor: themeColor, color: '#fff' } : { backgroundColor: '#f3f4f6', color: '#6b7280' }}
                >
                  +{MoneyUtils.format(option.extraPrice)}
                </span>
              )}
              {(!option.extraPrice || option.extraPrice === 0) && (
                <span className="text-[10px] text-gray-400 ml-1 normal-case">Free</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const MenuItemModal = ({ isOpen, onClose, editCartItem = null }) => {
  const { addItem, updateQuantity, removeItem, menuItemModal } = useCartContext();
  const { item } = menuItemModal || {};
  const { theme, selfOrderConfig, capabilities } = useRestaurantContext();

  const isEditMode = !!editCartItem;
  const isOrderingEnabled = capabilities?.isOrderingEnabled ?? true;

  const [quantity, setQuantity] = useState(1);
  const [selectedPortion, setSelectedPortion] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [selectedComboOptions, setSelectedComboOptions] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (isOpen && item) {
      document.body.style.overflow = 'hidden';

      if (isEditMode && editCartItem) {
        // Pre-fill from the existing cart entry
        setQuantity(editCartItem.quantity || 1);
        const cust = editCartItem.customizations || {};
        if (cust.portion) {
          setSelectedPortion(cust.portion);
        } else if (item.portions && item.portions.length > 0) {
          setSelectedPortion(item.portions[0]);
        } else {
          setSelectedPortion(null);
        }
        // Rebuild selectedAddOns map keyed by addon name
        const addonsArray = cust.addons || cust.addOns || [];
        const addonsMap = {};
        addonsArray.forEach((a) => { addonsMap[a.name] = { ...a }; });
        setSelectedAddOns(addonsMap);
        setSpecialInstructions(editCartItem.specialInstructions || '');
        setSelectedComboOptions(buildInitialComboSelections(item.comboComponents));
      } else {
        setQuantity(1);
        if (item._preSelectedPortion) {
          setSelectedPortion(item._preSelectedPortion);
        } else if (item.portions && item.portions.length > 0) {
          setSelectedPortion(item.portions[0]);
        } else {
          setSelectedPortion(null);
        }
        setSelectedAddOns({});
        setSelectedComboOptions(buildInitialComboSelections(item.comboComponents));
        setSpecialInstructions('');
      }
      setIsAdding(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, item, isEditMode, editCartItem]);

  const showOffer = item?.price && item?.offerPrice && item.offerPrice < item.price;
  const discount = showOffer
    ? Math.round(((item.price - (item?.offerPrice ?? 0)) / item.price) * 100)
    : null;

  const basePrice = useMemo(() => {
    if (selectedPortion) return selectedPortion.price;
    if (showOffer) return item?.offerPrice ?? 0;
    return item?.price ?? 0;
  }, [item, selectedPortion, showOffer]);

  // Sum of extra prices from chosen combo options
  const comboExtraTotal = useMemo(() => {
    return Object.values(selectedComboOptions).reduce(
      (sum, sel) => sum + (sel?.extraPrice || 0),
      0
    );
  }, [selectedComboOptions]);

  const totalPrice = useMemo(() => {
    let unitPrice = basePrice + comboExtraTotal;
    Object.values(selectedAddOns).forEach((addon) => {
      unitPrice += (addon.price || 0) * (addon.quantity || 1);
    });
    return unitPrice * quantity;
  }, [basePrice, comboExtraTotal, quantity, selectedAddOns]);

  // Combo: check all option-bearing components have a selection
  const allComboSelectionsComplete = useMemo(() => {
    if (!item?.isCombo || !item?.comboComponents) return true;
    return item.comboComponents.every((comp, idx) => {
      if (!comp.options || comp.options.length === 0) return true; // fixed component
      return !!selectedComboOptions[idx]; // must have a selection
    });
  }, [item, selectedComboOptions]);

  const handleComboOptionSelect = (componentIndex, selection) => {
    setSelectedComboOptions((prev) => ({ ...prev, [componentIndex]: selection }));
  };

  const handleDragEnd = (_, info) => {
    if (info.offset.y > 120 || info.velocity.y > 600) {
      onClose();
    }
  };

  const handleAddToCart = async () => {
    if (!item || !allComboSelectionsComplete) return;
    setIsAdding(true);

    // Build resolved combo components with selected options
    const resolvedComboComponents = item.isCombo && item.comboComponents
      ? item.comboComponents.map((comp, idx) => {
          const sel = selectedComboOptions[idx];
          return {
            name: comp.name,
            category: comp.category,
            quantity: comp.quantity ?? 1,
            instructions: comp.instructions,
            menuItemId: comp.menuItemId,
            options: comp.options,
            selectedOptionName: sel?.optionName || null,
            selectedOptionExtraPrice: sel?.extraPrice || 0,
            selectedMenuItemId: sel?.menuItemId || null,
          };
        })
      : undefined;

    const customizations = {};
    if (selectedPortion) customizations.portion = selectedPortion;
    if (Object.keys(selectedAddOns).length > 0)
      customizations.addons = Object.values(selectedAddOns);
    if (resolvedComboComponents) customizations.comboComponents = resolvedComboComponents;

    // Item price includes combo extra (mirrors Flutter's comboExtraPrice behavior)
    const { _preSelectedPortion, ...cleanItem } = item;
    const cleanBase = { ...cleanItem, price: basePrice + comboExtraTotal };

    try {
      if (isEditMode && editCartItem) {
        // In edit mode: remove the old entry, add new one with updated customizations
        await removeItem(editCartItem.cartItemId);
        const customizationDetails =
          selectedPortion ||
          Object.keys(selectedAddOns).length > 0 ||
          resolvedComboComponents ||
          specialInstructions
            ? {
                quantity,
                unitBasePrice: basePrice + comboExtraTotal,
                customizations,
                specialInstructions,
              }
            : { quantity, unitBasePrice: basePrice + comboExtraTotal, customizations: {}, specialInstructions: '' };
        await addItem(cleanBase, customizationDetails);
      } else {
        const customizationDetails =
          selectedPortion ||
          Object.keys(selectedAddOns).length > 0 ||
          resolvedComboComponents ||
          specialInstructions
            ? {
                quantity,
                unitBasePrice: basePrice + comboExtraTotal,
                customizations,
                specialInstructions,
              }
            : null;
        await addItem(cleanBase, customizationDetails);
      }
      onClose();
    } catch (err) {
      console.error('Failed to add to cart', err);
    } finally {
      setIsAdding(false);
    }
  };

  const themeColor = theme?.secondary || '#000';
  const themeTextColor = theme?.primary || '#fff';

  const hasPortions = item?.portions && item.portions.length > 0;
  const hasAddOns = (item?.addons && item.addons.length > 0) || (item?.addOns && item.addOns.length > 0);
  const hasCombo = item?.isCombo && item?.comboComponents && item.comboComponents.length > 0;

  // Split combo components: fixed (no options) vs selectable
  const fixedComponents = hasCombo ? item.comboComponents.filter((c) => !c.options || c.options.length === 0) : [];
  const selectableComponents = hasCombo ? item.comboComponents.filter((c) => c.options && c.options.length > 0) : [];

  // ── Dynamic height: estimate rows then clamp to 75vh ──────────────────────
  // Each row ≈ 52px, header ≈ 130px, bottom bar ≈ 68px, special instructions ≈ 80px
  const portionCount = hasPortions ? item.portions.length : 0;
  const addonCount   = hasAddOns   ? (item.addons || item.addOns).length : 0;
  const comboCount   = hasCombo    ? item.comboComponents.length : 0;
  const ROW_H    = 52;
  const HEADER_H = 130;
  const BOTTOM_H = isOrderingEnabled ? 72 : 0;
  const INSTR_H  = isOrderingEnabled ? 88 : 0;
  const SECTION_HEADERS = (hasPortions ? 62 : 0) + (hasAddOns ? 62 : 0) + (hasCombo ? 62 : 0);
  const estimatedH = HEADER_H + SECTION_HEADERS + (portionCount + addonCount + comboCount) * ROW_H + INSTR_H + BOTTOM_H + 24;
  const sheetMaxH  = Math.round(Math.min(window.innerHeight * 0.75, estimatedH));

  const sheet = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex justify-center items-end"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Sheet — height driven by content, capped at 75vh */}
          <motion.div
            className="bg-white rounded-t-2xl w-full max-w-md shadow-xl relative flex flex-col overflow-hidden"
            style={{ maxHeight: `${sheetMaxH}px`, height: `${sheetMaxH}px` }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* ── Header: image + name/price ── */}
            <div className="flex items-center gap-3 px-4 pt-2 pb-3 flex-shrink-0 border-b border-gray-100">
              <div className="w-20 h-20 rounded-xl flex-shrink-0 overflow-hidden bg-gray-100">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-300">
                    <Icon 
                      name={/(drink|beverage|juice|coffee|tea|shake|smoothie|water)/i.test(item?.category || item?.name) ? 'Coffee' : 'Utensils'} 
                      size={32} 
                      strokeWidth={1.5}
                    />
                  </div>
                )}

              </div>

              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold text-gray-900 leading-tight">{capitalizeFirst(item.name)}</h2>
                {item.description && (
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{item.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-sm font-bold">
                    {selectedPortion
                      ? MoneyUtils.format(selectedPortion.price + comboExtraTotal)
                      : showOffer
                        ? MoneyUtils.format((item.offerPrice ?? 0) + comboExtraTotal)
                        : MoneyUtils.format((item.price ?? 0) + comboExtraTotal)}
                  </span>
                  {showOffer && (
                    <span className="text-xs text-gray-400 line-through">
                      {MoneyUtils.format(item.price)}
                    </span>
                  )}
                  {discount && (
                    <span className="text-[10px] font-semibold px-1 py-0.5 rounded border border-gray-300">
                      {discount}% OFF
                    </span>
                  )}
                  {/* Combo badge */}
                  {hasCombo && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                      COMBO
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400">
                  {item.rating && <span>⭐ {item.rating}</span>}
                  {item.prepTime && <span>⏱ {item.prepTime} mins</span>}
                </div>
              </div>
            </div>

            {/* ── Scrollable body — fills remaining space between header and bottom bar ── */}
            <div className="overflow-y-auto flex-1 [&::-webkit-scrollbar]:hidden">
              <div className="px-3 pt-2 pb-3 space-y-3">

                {/* ── COMBO SECTION ─────────────────────────────────────── */}
                {hasCombo && (
                  <div>
                    {/* Fixed components (display-only) */}
                    {fixedComponents.length > 0 && (
                      <div className="bg-orange-50 rounded-xl p-3 border border-orange-100 mb-3">
                        <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wide mb-2">
                          Combo Includes
                        </p>
                        <div className="space-y-1">
                          {fixedComponents.map((comp, idx) => (
                            <ComboComponentPicker
                              key={`fixed-${idx}`}
                              component={comp}
                              componentIndex={item.comboComponents.indexOf(comp)}
                              selectedOption={null}
                              onSelect={() => {}}
                              themeColor={themeColor}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Selectable components */}
                    {selectableComponents.map((comp) => {
                      const realIdx = item.comboComponents.indexOf(comp);
                      return (
                        <div key={`sel-${realIdx}`} className="mb-4">
                          <ComboComponentPicker
                            component={comp}
                            componentIndex={realIdx}
                            selectedOption={selectedComboOptions[realIdx] || null}
                            onSelect={handleComboOptionSelect}
                            themeColor={themeColor}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── PORTIONS ───────────────────────────────────────────── */}
                {hasPortions && (
                  <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                    {/* Section header */}
                    <div className="px-2 pt-2 pb-1.5 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-black text-gray-900">
                          {isOrderingEnabled ? 'Select Size' : 'Available Sizes'}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Select 1 option</p>
                      </div>
                      {isOrderingEnabled && selectedPortion && (
                        <button
                          onClick={() => setSelectedPortion(null)}
                          className="text-[11px] font-bold"
                          style={{ color: themeColor }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="h-px bg-gray-200 mx-2" />

                    {/* Rows */}
                    <div className="divide-y divide-gray-100">
                      {item.portions.map((portion, idx) => {
                        const isSelected =
                          isOrderingEnabled &&
                          selectedPortion?.name === portion.name &&
                          selectedPortion?.price === portion.price;
                        const isUnavailable = portion.isAvailable === false;
                        const isClickable = isOrderingEnabled && !isUnavailable;

                        const isVeg = portion.isVeg === true || portion.isVegetarian === true;

                        return (
                          <div
                            key={idx}
                            onClick={() => isClickable && setSelectedPortion(portion)}
                            className={`flex items-center gap-2 px-2 py-2 transition-colors ${
                              isUnavailable
                                ? 'opacity-40 cursor-not-allowed'
                                : isClickable
                                  ? 'cursor-pointer active:bg-gray-100'
                                  : 'cursor-default'
                            }`}
                          >
                            {/* Veg / Non-Veg square indicator */}
                            <div
                              className={`w-4 h-4 flex-shrink-0 border-2 rounded-sm flex items-center justify-center ${
                                isVeg ? 'border-green-600' : 'border-red-600'
                              }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  isVeg ? 'bg-green-600' : 'bg-red-600'
                                }`}
                              />
                            </div>

                            {/* Name — truncates with ellipsis */}
                            <span className={`flex-1 min-w-0 truncate text-sm font-medium text-gray-700 ${ isUnavailable ? 'line-through' : '' }`}>
                              {capitalizeFirst(portion.name || portion.size)}
                            </span>

                            {/* Price — fixed, no shrink */}
                            <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                              {MoneyUtils.format(portion.price)}
                            </span>

                            {/* Radio circle */}
                            {isOrderingEnabled && (
                              <div
                                className={`w-5 h-5 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${
                                  isSelected ? 'border-current' : 'border-gray-300'
                                }`}
                                style={isSelected ? { borderColor: themeColor } : {}}
                              >
                                {isSelected && (
                                  <div
                                    className="w-2.5 h-2.5 rounded-full"
                                    style={{ backgroundColor: themeColor }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── ADD-ONS ────────────────────────────────────────────── */}
                {hasAddOns && (
                  <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                    {/* Section header */}
                    <div className="px-2 pt-2 pb-1.5 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-black text-gray-900">
                          {isOrderingEnabled ? 'Add Ons' : 'Available Add-Ons'}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Optional</p>
                      </div>
                      {isOrderingEnabled && Object.keys(selectedAddOns).length > 0 && (
                        <button
                          onClick={() => setSelectedAddOns({})}
                          className="text-[11px] font-bold"
                          style={{ color: themeColor }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="h-px bg-gray-200 mx-2" />

                    <div className="divide-y divide-gray-100">
                      {(item.addons || item.addOns).map((addon, idx) => {
                        const isSelected = isOrderingEnabled && !!selectedAddOns[addon.name];
                        const currentQuantity = selectedAddOns[addon.name]?.quantity || 0;
                        const isMaxReached = addon.maxLimit ? currentQuantity >= addon.maxLimit : false;
                        const isUnavailable = addon.isAvailable === false;

                        const isVeg = addon.isVeg === true || addon.isVegetarian === true;

                        const handleToggle = () => {
                          if (!isOrderingEnabled || isUnavailable || addon.canAddMultiple) return;
                          setSelectedAddOns((prev) => {
                            const next = { ...prev };
                            if (next[addon.name]) {
                              delete next[addon.name];
                            } else {
                              next[addon.name] = { ...addon, quantity: 1 };
                            }
                            return next;
                          });
                        };

                        return (
                          <div
                            key={idx}
                            onClick={handleToggle}
                            className={`flex items-center gap-2 px-2 py-2 transition-colors ${
                              isUnavailable
                                ? 'opacity-40 cursor-not-allowed'
                                : !addon.canAddMultiple && isOrderingEnabled
                                  ? 'cursor-pointer active:bg-gray-100'
                                  : 'cursor-default'
                            }`}
                          >
                            {/* Veg / Non-Veg square indicator */}
                            <div
                              className={`w-4 h-4 flex-shrink-0 border-2 rounded-sm flex items-center justify-center ${
                                isVeg ? 'border-green-600' : 'border-red-600'
                              }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  isVeg ? 'bg-green-600' : 'bg-red-600'
                                }`}
                              />
                            </div>

                            {/* Name — truncates with ellipsis */}
                            <span className={`flex-1 min-w-0 truncate text-sm font-medium text-gray-700 ${ isUnavailable ? 'line-through' : '' }`}>
                              {capitalizeFirst(addon.name)}
                            </span>

                            {/* Price — fixed, no shrink */}
                            <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                              +{MoneyUtils.format(addon.price)}
                            </span>

                            {/* Right control: checkbox OR stepper */}
                            {isOrderingEnabled && (
                              addon.canAddMultiple ? (
                                /* Inline stepper for multi-qty addons */
                                <div
                                  className="flex items-center gap-1.5 ml-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {isSelected ? (
                                    <div
                                      className="flex items-center gap-2 rounded-full border px-2 py-0.5"
                                      style={{ borderColor: themeColor }}
                                    >
                                      <button
                                        className="w-4 h-4 flex items-center justify-center font-bold text-sm leading-none"
                                        style={{ color: themeColor }}
                                        onClick={() =>
                                          setSelectedAddOns((prev) => {
                                            const next = { ...prev };
                                            if (next[addon.name].quantity > 1) {
                                              next[addon.name] = { ...next[addon.name], quantity: next[addon.name].quantity - 1 };
                                            } else {
                                              delete next[addon.name];
                                            }
                                            return next;
                                          })
                                        }
                                      >−</button>
                                      <span className="text-xs font-black min-w-[12px] text-center" style={{ color: themeColor }}>
                                        {currentQuantity}
                                      </span>
                                      <button
                                        className="w-4 h-4 flex items-center justify-center rounded-full font-bold text-sm leading-none text-white"
                                        style={isMaxReached ? { backgroundColor: '#d1d5db' } : { backgroundColor: themeColor }}
                                        disabled={isMaxReached}
                                        onClick={() => {
                                          if (isMaxReached) return;
                                          setSelectedAddOns((prev) => ({
                                            ...prev,
                                            [addon.name]: { ...prev[addon.name], quantity: prev[addon.name].quantity + 1 },
                                          }));
                                        }}
                                      >+</button>
                                    </div>
                                  ) : (
                                    <button
                                      className="text-xs font-bold px-2.5 py-1 rounded-full border"
                                      style={{ borderColor: themeColor, color: themeColor }}
                                      disabled={isUnavailable}
                                      onClick={() => {
                                        if (isUnavailable) return;
                                        setSelectedAddOns((prev) => ({
                                          ...prev,
                                          [addon.name]: { ...addon, quantity: 1 },
                                        }));
                                      }}
                                    >
                                      Add
                                    </button>
                                  )}
                                </div>
                              ) : (
                                /* Checkbox square */
                                <div
                                  className={`ml-1 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all ${
                                    isSelected ? 'border-current' : 'border-gray-300 bg-white'
                                  }`}
                                  style={isSelected ? { borderColor: themeColor, backgroundColor: themeColor } : {}}
                                >
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── SPECIAL INSTRUCTIONS ───────────────────────────────── */}
                {isOrderingEnabled && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Special Instructions</p>
                    <textarea
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:border-gray-400"
                      rows={2}
                      placeholder="E.g. Less spicy, no onions..."
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ── Fixed bottom: qty + add to cart ── */}
            {isOrderingEnabled && (
              <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-3 flex items-center gap-3">
                {/* Quantity stepper */}
                <div
                  className="flex items-center gap-3 border-2 rounded-full px-3 py-1.5"
                  style={{ borderColor: themeColor }}
                >
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-5 h-5 flex items-center justify-center font-bold text-base"
                    style={{ color: themeColor }}
                  >
                    −
                  </button>
                  <span className="text-sm font-bold w-4 text-center" style={{ color: themeColor }}>
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="w-5 h-5 flex items-center justify-center rounded-full font-bold text-base text-white"
                    style={{ backgroundColor: themeColor, color: themeTextColor }}
                  >
                    +
                  </button>
                </div>

                {/* Add to cart button */}
                <button
                  onClick={handleAddToCart}
                  disabled={isAdding || !allComboSelectionsComplete || item?.isAvailable === false}
                  className="flex-1 rounded-full py-3 text-sm font-bold transition-opacity"
                  style={{
                    backgroundColor: themeColor,
                    color: themeTextColor,
                    opacity: (isAdding || !allComboSelectionsComplete || item?.isAvailable === false) ? 0.6 : 1,
                  }}
                >
                {isAdding
                    ? 'Adding...'
                    : !allComboSelectionsComplete
                      ? 'Please Select Options ↑'
                      : isEditMode
                        ? `Save Changes  •  ${MoneyUtils.format(totalPrice)}`
                        : `Add  •  ${MoneyUtils.format(totalPrice)}`}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(sheet, document.body);
};

export default MenuItemModal;